/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	FormProvider,
	SubmitHandler,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Upload, Bell, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import {
	AnnouncementFormValues,
	BlockFileEntry
} from "../../adapters/formValuesToAnnouncement";
import { safeImageUrl } from "src/utils/safeImageUrl";
import PushPreviewPanel from "../PushPreviewPanel/PushPreviewPanel";
import AudiencePicker from "../AudiencePicker/AudiencePicker";
import ContentBlocksEditor, {
	ContentBlock
} from "../ContentBlocksEditor/ContentBlocksEditor";
import { parseBodyToBlocks } from "../../utils/announcementFormHelpers";

export type { AnnouncementFormValues };

interface Props {
	defaultValues: AnnouncementFormValues;
	onSubmit: (data: AnnouncementFormValues, imageRemoved: boolean) => void;
	onSendOrSchedule?: (
		data: AnnouncementFormValues,
		imageRemoved: boolean
	) => void;
	isSubmitting?: boolean;
	isSending?: boolean;
	isDeleting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageUrl?: string | null;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 2_097_152; // 2 MB

const AnnouncementForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	onSendOrSchedule,
	isSubmitting = false,
	isSending = false,
	isDeleting = false,
	isEdit = false,
	onDelete,
	existingImageUrl
}) => {
	const methods = useForm<AnnouncementFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue, trigger } = methods;
	const { isDirty, errors } = useFormState({ control });

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>(() =>
		parseBodyToBlocks(defaultValues.body, defaultValues.contentBlocks)
	);

	const notificationType = watch("notificationType");
	const isFullAnnouncement = notificationType === "announcement";

	const heroImage = watch("heroImage");
	const pushTitle = watch("pushTitle");
	const pushBody = watch("pushBody");
	const scheduleType = watch("scheduleType");
	const hasExpiry = watch("hasExpiry");
	const scheduledDate = watch("scheduledDate");
	const audienceType = watch("audienceType");
	const audiencePlatform = watch("audiencePlatform");
	const audienceUserIds = watch("audienceUserIds");

	const safeExistingUrl = safeImageUrl(existingImageUrl);
	const displayUrl = imageRemoved ? null : previewUrl || safeExistingUrl;

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		isSending ||
		isDeleting ||
		!!imageError;

	const isSendBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		isDeleting ||
		isSending ||
		!!imageError;

	const todayLocal = () => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	};

	const escapeAttr = (s: string) =>
		s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

	const syncBlocksToForm = (blocks: ContentBlock[], dirty: boolean) => {
		const bodyHtml = blocks
			.map((b, i) => {
				if (b.type === "text") {
					return b.content || "";
				}
				if (b.type === "image") {
					if (b.file) {
						return `<img data-block-index="${i}" alt="Uploaded image">`;
					}
					return b.content
						? `<img src="${escapeAttr(b.content)}" alt="Uploaded image">`
						: "";
				}
				if (b.type === "video") {
					if (b.file) {
						return `<video data-block-index="${i}" controls aria-label="Uploaded video"></video>`;
					}
					return b.content
						? `<video controls aria-label="Uploaded video"><source src="${escapeAttr(b.content)}"></video>`
						: "";
				}
				return "";
			})
			.filter(Boolean)
			.join("");
		setValue("body", bodyHtml, { shouldDirty: dirty });

		const blockFiles: BlockFileEntry[] = blocks
			.map((b, i) =>
				b.file && (b.type === "image" || b.type === "video")
					? { index: i, type: b.type, file: b.file }
					: null
			)
			.filter((entry): entry is BlockFileEntry => entry !== null);
		setValue("blockFiles", blockFiles, { shouldDirty: dirty });

		const serializedBlocks: MeeAnnouncementContentBlock[] = blocks.map(
			(b, i) => ({
				type: b.type,
				content:
					b.type === "text"
						? b.content || ""
						: b.file
							? `__block_file_${i}__`
							: b.content || ""
			})
		);
		setValue("contentBlocks", serializedBlocks, { shouldDirty: dirty });
	};

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
		const parsed = parseBodyToBlocks(
			defaultValues.body,
			defaultValues.contentBlocks
		);
		setContentBlocks(parsed);
		syncBlocksToForm(parsed, false);
	}, [defaultValues]); // eslint-disable-line

	const scheduledTime = watch("scheduledTime");

	useEffect(() => {
		trigger("scheduledDate");
		trigger("expiryDate");
	}, [scheduledDate, scheduledTime, scheduleType, trigger]);

	const handleBlocksChange = (blocks: ContentBlock[]) => {
		setContentBlocks(blocks);
		syncBlocksToForm(blocks, true);
	};

	useEffect(() => {
		if (heroImage) {
			const url = URL.createObjectURL(heroImage);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
		}
	}, [heroImage]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 2 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		setValue("heroImage", file, { shouldDirty: true });
		setImageRemoved(false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("heroImage", null, { shouldDirty: true });
		setPreviewUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleFormSubmit: SubmitHandler<AnnouncementFormValues> = (data) => {
		onSubmit(data, imageRemoved);
	};

	const handleSendOrSchedule = () => {
		handleSubmit((data) => {
			if (onSendOrSchedule) {
				onSendOrSchedule(data, imageRemoved);
			}
		})();
	};

	const detailsHasErrors = !!(errors.title || errors.body);
	const pushHasErrors = !!(errors.pushTitle || errors.pushBody);
	const audienceHasErrors = !!(
		errors.audienceType ||
		errors.audiencePlatform ||
		errors.audienceUserIds
	);
	const schedulingHasErrors = !!(errors.scheduledDate || errors.scheduledTime);
	const expiryHasErrors = !!(errors.expiryDate || errors.expiryTime);

	return (
		<form
			onSubmit={handleSubmit(handleFormSubmit)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Type toggle */}
					<fieldset>
						<legend className="sr-only">Notification type</legend>
						<div className="inline-flex w-full gap-1 p-1.5 bg-white border border-border rounded-xl shadow-xs">
							{(
								[
									{
										value: "push",
										icon: <Bell className="h-4 w-4" />,
										label: "Push Only"
									},
									{
										value: "announcement",
										icon: <FileText className="h-4 w-4" />,
										label: "Announcement"
									}
								] as const
							).map((option) => {
								const isChecked = notificationType === option.value;
								return (
									<label key={option.value} className="flex-1 cursor-pointer">
										<input
											type="radio"
											className="sr-only"
											value={option.value}
											checked={isChecked}
											onChange={() =>
												setValue("notificationType", option.value, {
													shouldDirty: true
												})
											}
										/>
										<div
											className={[
												"flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
												isChecked
													? "bg-primary text-white shadow-md shadow-primary/50"
													: "text-muted-foreground hover:text-foreground hover:bg-white/80"
											].join(" ")}
										>
											{option.icon}
											<span>{option.label}</span>
										</div>
									</label>
								);
							})}
						</div>
					</fieldset>

					{/* Card 1: Details (announcement only) */}
					{isFullAnnouncement && (
						<div
							className={[
								"rounded-xl border bg-subtle shadow-sm transition-colors",
								detailsHasErrors ? "border-red-400/60" : "border-border"
							].join(" ")}
						>
							{detailsHasErrors && (
								<div className="relative">
									<span
										role="alert"
										className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
									>
										Please fill out the required fields
									</span>
								</div>
							)}
							<div className="p-6">
								<label className="font-heading text-lg font-medium text-foreground mb-4 block">
									Details
								</label>

								<div className="flex flex-col gap-5">
									<FormField
										control={control}
										name="title"
										rules={{
											required: "Title is required",
											maxLength: {
												value: 100,
												message: "Maximum 100 characters"
											}
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Title
												</FormLabel>
												<FormControl>
													<Input
														placeholder="Announcement title"
														maxLength={100}
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
												<p className="text-[13px] text-zinc-500 text-right mt-1">
													{field.value?.length || 0} / 100
												</p>
											</FormItem>
										)}
									/>

									{/* Hero image */}
									<div>
										<p className="text-sm font-medium text-zinc-600 mb-2">
											Hero Image (optional)
										</p>
										<p className="text-[13px] text-muted-foreground mb-3">
											JPG or PNG, 1200 x 628px recommended, under 2 MB
										</p>
										<input
											ref={fileInputRef}
											type="file"
											accept="image/jpeg,image/jpg,image/png"
											className="hidden"
											onChange={handleFileChange}
										/>
										<div className="max-w-[480px]">
											{displayUrl ? (
												<>
													<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
														<img
															src={displayUrl}
															alt="Hero image preview"
															className="object-contain w-full h-auto max-h-[400px]"
														/>
													</div>
													<div className="flex items-center gap-2">
														<button
															type="button"
															className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
															onClick={() => fileInputRef.current?.click()}
														>
															Change image
														</button>
														<button
															type="button"
															className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
															onClick={handleRemoveImage}
														>
															Remove
														</button>
													</div>
												</>
											) : (
												<div
													role="button"
													tabIndex={0}
													onClick={() => fileInputRef.current?.click()}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															fileInputRef.current?.click();
														}
													}}
													onDragOver={handleDragOver}
													onDragLeave={handleDragLeave}
													onDrop={handleDrop}
													className={[
														"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
														isDragging
															? "border-primary bg-primary/10"
															: "border-border bg-white hover:border-primary hover:bg-primary/5"
													].join(" ")}
												>
													<Upload className="h-6 w-6 text-muted-foreground" />
													<div>
														<p className="text-sm font-medium text-foreground">
															Drag image here or click to browse
														</p>
														<p className="text-xs text-muted-foreground mt-1">
															JPG or PNG, under 2 MB
														</p>
													</div>
												</div>
											)}
										</div>
										{imageError && (
											<p className="text-sm text-red-500 mt-2">{imageError}</p>
										)}
									</div>

									{/* Content blocks */}
									<div>
										<p className="text-sm font-medium text-zinc-600 mb-2">
											Content
										</p>
										<p className="text-[13px] text-muted-foreground mb-3">
											Add and reorder text, images, and videos
										</p>
										<ContentBlocksEditor
											blocks={contentBlocks}
											onChange={handleBlocksChange}
										/>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Card 2: Push Notification */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							pushHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{pushHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required fields
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-1 block">
								Push Notification
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								These fields populate the push notification sent to devices
							</p>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="pushTitle"
									rules={{
										required: "Push title is required",
										maxLength: {
											value: 50,
											message: "Maximum 50 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Push Title
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Notification title"
													maxLength={50}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 50
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="pushBody"
									rules={{
										maxLength: {
											value: 150,
											message: "Maximum 150 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Push Body
											</FormLabel>
											<FormControl>
												<textarea
													placeholder="Short notification summary"
													maxLength={150}
													rows={3}
													className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none"
													onInput={(e) => {
														const target = e.target as HTMLTextAreaElement;
														target.style.height = "auto";
														target.style.height = `${target.scrollHeight}px`;
													}}
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 150
											</p>
										</FormItem>
									)}
								/>
								{/* Inline push preview */}
								<div className="mt-2">
									<PushPreviewPanel
										pushTitle={pushTitle || ""}
										pushBody={pushBody || ""}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Card 3: Audience */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							audienceHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{audienceHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please select an audience
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Audience
							</label>

							<AudiencePicker
								audienceType={audienceType}
								audiencePlatform={audiencePlatform}
								audienceUserIds={audienceUserIds}
								onAudienceTypeChange={(type) =>
									setValue("audienceType", type, { shouldDirty: true })
								}
								onPlatformChange={(platform) =>
									setValue("audiencePlatform", platform, {
										shouldDirty: true
									})
								}
								onUserIdsChange={(ids) =>
									setValue("audienceUserIds", ids, { shouldDirty: true })
								}
							/>
						</div>
					</div>

					{/* Card 4: Scheduling */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							schedulingHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{schedulingHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.scheduledDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Scheduling
							</label>

							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-2.5">
									<label className="flex items-center gap-2.5 cursor-pointer">
										<input
											type="radio"
											name="scheduleType"
											value="now"
											checked={scheduleType === "now"}
											onChange={() =>
												setValue("scheduleType", "now", {
													shouldDirty: true
												})
											}
											className="h-4 w-4 text-primary border-border focus:ring-primary"
										/>
										<span className="text-sm text-foreground">
											Send immediately
										</span>
									</label>
									<label className="flex items-center gap-2.5 cursor-pointer">
										<input
											type="radio"
											name="scheduleType"
											value="scheduled"
											checked={scheduleType === "scheduled"}
											onChange={() =>
												setValue("scheduleType", "scheduled", {
													shouldDirty: true
												})
											}
											className="h-4 w-4 text-primary border-border focus:ring-primary"
										/>
										<span className="text-sm text-foreground">
											Schedule for later
										</span>
									</label>
								</div>

								{scheduleType === "scheduled" && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
										<FormField
											control={control}
											name="scheduledDate"
											rules={{
												required:
													scheduleType === "scheduled"
														? "Scheduled date is required"
														: false,
												validate: (value: string) => {
													if (scheduleType !== "scheduled") return true;
													const scheduledTime = watch("scheduledTime");
													if (value) {
														const scheduled = new Date(
															`${value}T${scheduledTime || "00:00"}`
														);
														if (scheduled <= new Date()) {
															return "Scheduled date must be in the future";
														}
													}
													return true;
												}
											}}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Date
													</FormLabel>
													<FormControl>
														<Input
															type="date"
															min={todayLocal()}
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>

										<FormField
											control={control}
											name="scheduledTime"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Time
													</FormLabel>
													<FormControl>
														<Input
															type="time"
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Card 5: Expiry */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							expiryHasErrors ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{expiryHasErrors && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.expiryDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Expiry
							</label>

							<div className="flex flex-col gap-4">
								<FormField
									control={control}
									name="hasExpiry"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Set an expiry date
											</FormLabel>
										</FormItem>
									)}
								/>

								{hasExpiry && (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
										<FormField
											control={control}
											name="expiryDate"
											rules={{
												required: hasExpiry ? "Expiry date is required" : false,
												validate: (value: string) => {
													if (!hasExpiry) return true;
													const scheduledDate = watch("scheduledDate");
													const scheduledTime = watch("scheduledTime");
													const expiryTime = watch("expiryTime");
													if (value) {
														const expiry = new Date(
															`${value}T${expiryTime || "00:00"}`
														);
														const baseline =
															scheduleType === "scheduled" && scheduledDate
																? new Date(
																		`${scheduledDate}T${scheduledTime || "00:00"}`
																	)
																: new Date();
														if (expiry <= baseline) {
															return scheduleType === "scheduled"
																? "Expiry must be after scheduled date"
																: "Expiry must be in the future";
														}
													}
													return true;
												}
											}}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Expiry date
													</FormLabel>
													<FormControl>
														<Input
															type="date"
															min={todayLocal()}
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>

										<FormField
											control={control}
											name="expiryTime"
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm font-medium text-zinc-600">
														Expiry time
													</FormLabel>
													<FormControl>
														<Input
															type="time"
															className="w-full bg-white"
															{...field}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/announcements"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to announcements
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<div className="flex items-center gap-3">
						<Button
							type="submit"
							variant="outline"
							disabled={isSubmitBtnDisabled}
						>
							{isSubmitting ? (
								<div className="w-4 h-4 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
							) : (
								"Save Draft"
							)}
						</Button>

						{onSendOrSchedule && (
							<Button
								type="button"
								onClick={handleSendOrSchedule}
								disabled={isSendBtnDisabled}
							>
								{isSending ? (
									<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
								) : scheduleType === "scheduled" ? (
									"Schedule"
								) : (
									"Send Now"
								)}
							</Button>
						)}
					</div>
				</div>
			</FormProvider>
		</form>
	);
};

export default AnnouncementForm;
